/* eslint-disable max-len */
import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./HackathonCarousel.module.scss";

type HackathonRole = "HACKER" | "MENTOR" | "JUDGE";

interface Hackathon {
  name: string;
  year: number;
  role: HackathonRole;
  prizes: readonly string[];
  url: string;
}

// Reverse-chronological log of every hackathon appearance, newest first so
// the carousel opens on the latest event and walks back in time to the
// right. Order drives the readout index (01..10); role reads top-right, the
// title leads the body with prize results right-aligned beneath it.
const HACKATHONS: readonly Hackathon[] = [
  {
    name: "EthGlobal Cannes",
    year: 2025,
    role: "HACKER",
    prizes: ["Mantle 1st Prize"],
    url: "https://ethglobal.com/events/cannes",
  },
  {
    name: "EthDAM",
    year: 2024,
    role: "MENTOR",
    prizes: [],
    url: "https://www.ethdam.com/",
  },
  {
    name: "EthGlobal Brussels",
    year: 2024,
    role: "HACKER",
    prizes: ["Uniswap 1st Prize", "LayerZero 2nd Prize"],
    url: "https://ethglobal.com/events/brussels",
  },
  {
    name: "EthDAM",
    year: 2023,
    role: "MENTOR",
    prizes: [],
    url: "https://www.ethdam.com/",
  },
  {
    name: "EthGlobal Lisbon",
    year: 2023,
    role: "HACKER",
    prizes: ["Aave 1st Prize", "1inch 1st Prize"],
    url: "https://ethglobal.com/events/lisbon",
  },
  {
    name: "EthPorto",
    year: 2023,
    role: "HACKER",
    prizes: ["1st Prize"],
    url: "https://ethporto.org/",
  },
  {
    name: "EthLisbon",
    year: 2022,
    role: "HACKER",
    prizes: ["WalletConnect 2nd Prize"],
    url: "https://www.ethlisbon.org/",
  },
  {
    name: "Odyssey Momentum",
    year: 2020,
    role: "HACKER",
    prizes: ["SSI Track Winner"],
    url: "https://www.hackathon.com/event/odyssey-momentum-2020---enter-your-portal-to-the-future-5e4a6be3eb4970001b3dc134",
  },
  {
    name: "VeChain DevSummit",
    year: 2019,
    role: "HACKER",
    prizes: ["2nd Prize"],
    url: "https://medium.com/vechain-foundation/vechain-summit-2019-the-first-global-developer-conference-of-vechain-was-held-in-san-francisco-3728ce468af1",
  },
  {
    name: "Neo Hackathon @KPN & TU Delft",
    year: 2018,
    role: "HACKER",
    prizes: ["2nd Prize"],
    url: "https://neo.org/event/details/47",
  },
];

const roleClass: Record<HackathonRole, string | undefined> = {
  HACKER: styles["role-hacker"],
  MENTOR: styles["role-mentor"],
  JUDGE: styles["role-judge"],
};

// One scroll page equals one card plus the flex gap. Reading the computed gap
// keeps the step accurate across the responsive breakpoints.
const getStep = (track: HTMLDivElement): number => {
  const first = track.firstElementChild as HTMLElement | null;
  if (!first) {
    return track.clientWidth;
  }
  const computed = window.getComputedStyle(track);
  const gap = parseFloat(computed.columnGap || computed.gap || "0") || 0;
  return first.offsetWidth + gap;
};

export const HackathonCarousel: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const settleRef = useRef<number>(0);
  const [active, setActive] = useState<number>(0);
  const [canPrev, setCanPrev] = useState<boolean>(false);
  const [canNext, setCanNext] = useState<boolean>(true);
  // Recompute the active card and the reachable ends from the live scroll
  // position. Runs on every (passive) scroll frame and on resize.
  const sync = useCallback((): void => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const step = getStep(track);
    const index = step > 0 ? Math.round(track.scrollLeft / step) : 0;
    const clamped = Math.min(Math.max(index, 0), HACKATHONS.length - 1);
    setActive(clamped);
    setCanPrev(track.scrollLeft > 1);
    setCanNext(
      track.scrollLeft < track.scrollWidth - track.clientWidth - 1,
    );
  }, []);

  // Native CSS snap is unreliable on some mobile engines (Safari's gap +
  // snap quirks), so after scrolling settles we nudge the track to the
  // nearest whole-card edge ourselves. The end-of-track rest position is
  // legal even though it is not start-aligned.
  const settle = useCallback((): void => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const step = getStep(track);
    if (step <= 0) {
      return;
    }
    const max = track.scrollWidth - track.clientWidth;
    const target = Math.min(Math.round(track.scrollLeft / step) * step, max);
    if (
      Math.abs(track.scrollLeft - max) > 2 &&
      Math.abs(track.scrollLeft - target) > 2
    ) {
      track.scrollTo({ left: target, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const onScroll = (): void => {
      sync();
      window.clearTimeout(settleRef.current);
      settleRef.current = window.setTimeout(settle, 160);
    };
    sync();
    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", sync);
    return (): void => {
      window.clearTimeout(settleRef.current);
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", sync);
    };
  }, [sync, settle]);

  // Page by whole cards only: the largest number of full steps that fits
  // the viewport, never a raw clientWidth that would land mid-card.
  const page = useCallback((direction: number): void => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const step = getStep(track);
    const perPage = Math.max(1, Math.floor(track.clientWidth / step));
    track.scrollBy({
      left: direction * perPage * step,
      behavior: "smooth",
    });
  }, []);

  const jumpTo = useCallback((index: number): void => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    track.scrollTo({ left: index * getStep(track), behavior: "smooth" });
  }, []);

  return (
    <div
      className={styles["carousel"]}
      role="region"
      aria-label="Hackathons">
      <div className={styles["viewport"]}>
        <button
          type="button"
          className={`${styles["nav"]} ${styles["nav-prev"]}`}
          aria-label="Previous hackathons"
          disabled={!canPrev}
          onClick={() => page(-1)}>
          <svg
            className={styles["chevron"]}
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false">
            <polyline
              points="15 4 7 12 15 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
          </svg>
        </button>

        <div className={styles["track"]} ref={trackRef}>
          {HACKATHONS.map((event, index) => (
            <div
              key={`${event.name}-${event.year}`}
              className={styles["card"]}>
              <span className={styles["reticle"]} aria-hidden="true" />
              <div className={styles["head"]}>
                <span className={styles["index"]}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span
                  className={`${styles["role"]} ${roleClass[event.role]}`}>
                  {event.role}
                </span>
              </div>
              <span className={styles["name"]}>{event.name}</span>
              <div className={styles["prizes"]}>
                {event.prizes.map((prize) => (
                  <span key={prize} className={styles["prize"]}>
                    {prize}
                  </span>
                ))}
              </div>
              <div className={styles["foot"]}>
                <span className={styles["year"]}>{event.year}</span>
                <a
                  className={styles["visit"]}
                  href={event.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Visit ${event.name} ${event.year}`}>
                  <span>VISIT</span>
                </a>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          className={`${styles["nav"]} ${styles["nav-next"]}`}
          aria-label="Next hackathons"
          disabled={!canNext}
          onClick={() => page(1)}>
          <svg
            className={styles["chevron"]}
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false">
            <polyline
              points="9 4 17 12 9 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
          </svg>
        </button>
      </div>

      <div className={styles["pips"]}>
        {HACKATHONS.map((event, index) => (
          <button
            key={`pip-${event.name}-${event.year}`}
            type="button"
            className={`${styles["pip"]} ${
              index === active ? styles["pip-active"] : ""
            }`}
            aria-label={`${event.name}, ${event.year}`}
            aria-current={index === active ? "true" : undefined}
            onClick={() => jumpTo(index)}
          />
        ))}
      </div>
    </div>
  );
};
